import type { LinkableTextFrame } from '@/lib/text-linking/types/linkable-text-frame'
import { chainSequenceByFrameId } from '@/lib/text-linking/text-linking-geometry'

export function hasOutgoing(chains: LinkableTextFrame[][], frameId: string): boolean {
  return chains.some((chain) => chain.some((f, i) => f.id === frameId && i < chain.length - 1))
}

export function hasIncoming(chains: LinkableTextFrame[][], frameId: string): boolean {
  return chains.some((chain) => chain.some((f, i) => f.id === frameId && i > 0))
}

/** Which frames get ports this render, and the chain numbering they show. */
export interface PortFrames {
  linkableFrames: LinkableTextFrame[]
  seqByFrameId: Map<string, number>
  portFrames: LinkableTextFrame[]
  portFrameIds: Set<string>
  lockedOutPortFrames: LinkableTextFrame[]
  lockedOutPortFrameIds: Set<string>
}

// Ports: linking mode shows all linkable frames; select tool shows the selected frame plus its
// chain siblings (WMPCM-6852 chain-highlight: selecting any chain member reveals the chain).
//
// A MULTI-selection shows no ports at all (WMPCM-9868). Linking is inherently a one-source /
// one-target gesture, so a "+" on each of several selected frames offers a link the next click
// cannot express, and the port squares crowd chrome a multi-select already fills with handles.
// Gated on the selected count alone — `chainHighlightIds` stays in the predicate so selecting a
// SINGLE chain member still reveals its whole chain's ports, which is what WMPCM-6852 added and
// this must not undo.
function resolveInteractivePortFrames({
  linkingActive,
  selectToolActive,
  selectedTextFrameCount,
  linkableFrames,
  selectedSet,
  chainHighlightIds,
}: {
  linkingActive: boolean
  selectToolActive: boolean
  selectedTextFrameCount: number
  linkableFrames: LinkableTextFrame[]
  selectedSet: Set<string>
  chainHighlightIds: Set<string>
}): LinkableTextFrame[] {
  if (linkingActive) return linkableFrames
  if (selectToolActive && selectedTextFrameCount <= 1) {
    return linkableFrames.filter((f) => selectedSet.has(f.id) || chainHighlightIds.has(f.id))
  }

  return []
}

/**
 * Derives, from the layer's props alone, every frame set the port passes render from. Rebuilt
 * from scratch each render on purpose — see the focus-handoff effect in `use-port-focus.ts`.
 */
export function resolvePortFrames({
  chains,
  allTextFrames,
  selectedTextIds,
  linkingActive,
  selectToolActive,
}: {
  chains: LinkableTextFrame[][]
  allTextFrames: LinkableTextFrame[]
  selectedTextIds: string[]
  linkingActive: boolean
  selectToolActive: boolean
}): PortFrames {
  const selectedSet = new Set(selectedTextIds)
  // `locked` and `parentGroupId` only — deliberately NOT `!f.locked && !f.frozen`. A frozen frame
  // must still expose its in/out ports so it can be linked to and from without being unfrozen
  // (WMPCM-6972); freeze blocks placement geometry, not linking. A group member's ports must NOT
  // show, even while isolated for editing: `canLink` already rejects linking a group member on
  // either side (WMPCM-7036, mirroring the grouping-side restriction in
  // `isLinkedTextFrame`), so offering the port here let a user drag a link that would
  // silently no-op on drop — the fix is to keep the option from ever looking available (WMPCM-10396).
  // See `canLink` (`text-chain-mutator/helpers/can-link.ts`) for the full rule this mirrors.
  const linkableFrames = allTextFrames.filter((f) => !f.locked && f.parentGroupId == null)

  const seqByFrameId = chainSequenceByFrameId(chains)
  // Every member of any chain the current selection touches — these siblings get ports too
  // (and, in TextManager, a border-only selection highlight) even though they aren't truly
  // selected themselves.
  const chainHighlightIds = new Set(
    chains
      .filter((chain) => chain.some((f) => selectedSet.has(f.id)))
      .flatMap((chain) => chain.map((f) => f.id))
  )
  // How many text frames the user has actually selected. Chain siblings revealed by
  // `chainHighlightIds` deliberately do not count — they are not selected themselves. Counts only
  // LINKABLE frames, so a text frame selected alongside a plain rectangle still shows its ports:
  // the rule is about multiple *text frames*, which is how WMPCM-9868 words it.
  const selectedTextFrameCount = linkableFrames.filter((f) => selectedSet.has(f.id)).length

  const portFrames = resolveInteractivePortFrames({
    linkingActive,
    selectToolActive,
    selectedTextFrameCount,
    linkableFrames,
    selectedSet,
    chainHighlightIds,
  })
  const portFrameIds = new Set(portFrames.map((f) => f.id))

  // A LOCKED chain member's out-port, in the same selection scope that gives an unlocked frame its
  // ports (WMPCM-10578 #2). Locked frames are rightly kept out of `linkableFrames` — they cannot
  // link or unlink — but their linked out-port square stays on screen, and it used to reach the
  // screen only through the passive, `aria-hidden` chain badges: a visible control with no role,
  // name or focus. It is now drawn as a disabled control instead. Counted against ALL selected
  // non-group text frames, not just linkable ones, so a multi-selection still shows no ports
  // (WMPCM-9868).
  const selectedAnyTextFrameCount = allTextFrames.filter(
    (f) => f.parentGroupId == null && selectedSet.has(f.id)
  ).length
  const lockedOutPortFrames =
    !linkingActive && selectToolActive && selectedAnyTextFrameCount <= 1
      ? allTextFrames.filter(
          (f) =>
            f.locked === true &&
            f.parentGroupId == null &&
            (selectedSet.has(f.id) || chainHighlightIds.has(f.id)) &&
            // Any chain member, not only one with an outgoing link: a chain's LAST frame shows the
            // "+" out-port while unlocked, and an overflowing one keeps a "+" on screen after
            // locking (the overflow badge sits exactly there), so it needs the same disabled control.
            seqByFrameId.has(f.id)
        )
      : []
  const lockedOutPortFrameIds = new Set(lockedOutPortFrames.map((f) => f.id))

  return {
    linkableFrames,
    seqByFrameId,
    portFrames,
    portFrameIds,
    lockedOutPortFrames,
    lockedOutPortFrameIds,
  }
}
